<template>
  <pf-empty-state v-if="paymentsState.updating">
    <pf-empty-state-header :title="$t('Caricamento dei pagamenti')" heading-level="h4">
      <template #icon>
        <pf-empty-state-icon>
          <pf-spinner />
        </pf-empty-state-icon>
      </template>
    </pf-empty-state-header>
  </pf-empty-state>

  <component :is="pendingOnly ? PfPageSection : 'section'" v-else-if="payments.length" class="pf-v6-u-mt-xl">
    <pf-title h="3" v-if="title">{{ title }}</pf-title>

    <pf-table compact striped>
      <pf-thead>
        <pf-tr>
          <pf-th>{{ $t('Descrizione') }}</pf-th>
          <pf-th>
            {{ $t('Importo') }}
          </pf-th>
          <pf-th>
            {{ $t('Data') }}
          </pf-th>
          <pf-th :screen-reader-text="$t('Azioni')" />
        </pf-tr>
      </pf-thead>
      <pf-tbody>
        <pf-tr v-for="payment in payments" :key="payment.id">
          <pf-td :data-label="$t('Descrizione')">
            <span v-if="payment.fattura">{{ payment.tipo }} nr. {{ payment.fattura }}: </span>
            {{ payment.descrizione }}
          </pf-td>
          <pf-td :data-label="$t('Importo')">{{ $currency(payment.amount, payment.currency) }}</pf-td>
          <pf-td :data-label="$t('Data')">
            {{ $date(payment.data) }} ({{ $datefromnow(payment.data) }})
            <pf-icon v-if="overdue(payment.data)" status="danger" :title="$t('Scadenza insoluta')">
              <warning-triangle-icon />
            </pf-icon>
          </pf-td>
          <pf-td class="pf-v6-u-text-align-end-on-md" :class="{ 'pf-v6-u-text-color-status-success': payment.tipo === 'Nota credito' }">
            <template v-if="payment.tipo != 'Nota credito'">
              <granted-link
                v-if="payment.amount && payment.pending"
                :to="{ name: 'payment', params: { id: payment.id } }"
                type="primary"
                small
              >
                <credit-card-icon />
                {{ $t('Paga ora') }}
              </granted-link>
              <template v-else>
                {{ $t('Pagamento saldato') }}
              </template>
            </template>
          </pf-td>
        </pf-tr>
      </pf-tbody>
    </pf-table>

    <pf-title h="4" v-if="title" class="pf-v6-u-mt-xl">{{ $t('Coordinate bancarie per pagamento tramite bonifico bancario') }}</pf-title>
    <p class="coordinate">{{ coordinateBancarie }}</p>
  </component>
</template>

<style scoped>
.coordinate {
  white-space: pre-line;
}
</style>

<script lang="ts" setup>
import { computed } from 'vue';
import { $t as _t } from '@/i18n';
import { asyncComputedList } from '@common/asyncComputed';
import { useLoginStore } from '@/store/login';
import { PaymentsResource } from '@/resources';
import GrantedLink from './granted-link.vue';
import CreditCardIcon from '@vue-patternfly/icons/credit-card-icon';
import { PfPageSection } from '@vue-patternfly/core';
import WarningTriangleIcon from '@vue-patternfly/icons/warning-triangle-icon';
import { parseDateTime } from "@common/date";
import { DateTime } from 'luxon';

const login = useLoginStore();

const props = withDefaults(defineProps<{
  invoiceId: number,
  title?: string,
  paymethod?: string,
  pendingOnly?: boolean,
  currency?: string,
}>(), {
  title: _t('Rate'),
});


const { state: paymentsState, result: payments } = asyncComputedList(() => {
  return new PaymentsResource({
    id_fattura: props.invoiceId,
    pending_only: props.pendingOnly ? 1 : 0,
  }).get();
});

const coordinateBancarie = computed(() => {
  const coordinate = login.fatturante.coordinate_bancarie;
  const currency = props.currency ?? login.currency;

  return _t('Pagamenti in {0}\n {1} \n Intestatario:  {2}', [
    currency === 'EUR' ? 'EURO' : 'CHF',
    currency === 'EUR' ? coordinate.banca_eur : coordinate.banca_chf,
    coordinate.intestatario,
  ]);
});

function overdue(d: string) {
  const date = parseDateTime(d);
  return date.diff(DateTime.local(), ['days']).days <= 0;
}
</script>
